import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTimes, faVideo, faImage, faPodcast, faSpinner, faExclamationTriangle, faCheckCircle, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { customToast } from '../utils/customToast';
import { getChoiceFieldOptions, getMediaCentreItems, sendToMediaCentre } from '../services/mediaService';
import GalleryBrowser from '../components/Galleries/GalleryBrowser';
import { galleryService } from '../services/galleryService';
import apiHelper from '../utils/apiHelper';
import axios from 'axios';
import './ModalSendToMediaCentre.css';

// Thumbnail Preview Component with Authentication
const ThumbnailPreview = ({ thumbnailItem }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

   

    useEffect(() => {
        const loadThumbnail = async () => {
 
            if (!thumbnailItem) return;

            try {

                setIsLoading(true);
                setError(null);

                // If it's already a blob URL, use it directly
                if (thumbnailItem.mediaUrl?.startsWith('blob:')) {
                   
                    setThumbnailUrl(thumbnailItem.mediaUrl);
                    setIsLoading(false);
                    return;
                }

                // If it's an external URL (not our API), use it directly
                if (thumbnailItem.mediaUrl?.startsWith('http') && 
                    !thumbnailItem.mediaUrl.includes(process.env.REACT_APP_API_URL)) {
                   
                    setThumbnailUrl(thumbnailItem.mediaUrl);
                    setIsLoading(false);
                    return;
                }

                // Get headers with authentication token
                const headers = await apiHelper.getHeaders('graph/getMediaContent');
                
                
                // Fetch the image with auth headers using axios (like GalleryBrowser)
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${thumbnailItem.name}`,
                    {
                        headers,
                        responseType: 'blob'
                    }
                );

               
                const url = URL.createObjectURL(response.data);
               
                setThumbnailUrl(url);
            } catch (err) {
                console.error('Error loading thumbnail:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        loadThumbnail();

        // Cleanup blob URL on unmount
        return () => {
            if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailItem]);

    if (isLoading) {
        return (
            <div className="d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                <FontAwesomeIcon icon={faSpinner} spin className="text-muted" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex align-items-center justify-content-center border rounded" style={{ width: '60px', height: '60px', backgroundColor: '#f8f9fa' }}>
                <FontAwesomeIcon icon={faImage} className="text-muted" />
            </div>
        );
    }

    return (
        <img 
            src={thumbnailUrl}
            alt={thumbnailItem.name}
            style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                borderRadius: '4px'
            }}
        />
    );
};

const ModalSendToMediaCentre = ({ show, onHide, mediaItem }) => {

    
    const [formData, setFormData] = useState({
        shortDescription: '',
        topicName: '',
        series: [], // 🎯 Changed to array for multi-selection
        productService: '',
        theme: '',
        entity: 'IF', // Default to IF
        duration: ''
    });
    const [isAddingNewTopic, setIsAddingNewTopic] = useState(false);
    const [customTopicName, setCustomTopicName] = useState('');
    const [isAddingNewProduct, setIsAddingNewProduct] = useState(false);
    const [customProductName, setCustomProductName] = useState('');
    const [isAddingNewTheme, setIsAddingNewTheme] = useState(false);
    const [customThemeName, setCustomThemeName] = useState('');
    const [isAddingNewEntity, setIsAddingNewEntity] = useState(false);
    const [customEntityName, setCustomEntityName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [choiceOptions, setChoiceOptions] = useState({
        series: [],
        products: [],
        themes: [],
        entities: [],
        mediaTypes: [],
        topicNames: []
    });
    const [loadingOptions, setLoadingOptions] = useState(true);

    // Thumbnail selection state
    const [selectedThumbnail, setSelectedThumbnail] = useState(null);
    const [showGalleryBrowser, setShowGalleryBrowser] = useState(false);
    const [galleries, setGalleries] = useState([]);
    const [loadingGalleries, setLoadingGalleries] = useState(false);

    // Duplicate check state
    const [mediaCentreItems, setMediaCentreItems] = useState([]);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    const [loadingDuplicateCheck, setLoadingDuplicateCheck] = useState(false);
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(true);

    // Load choice field options and check duplicates when modal opens
    useEffect(() => {
        if (show && mediaItem) {
            setShowDuplicateWarning(true); // Reset warning visibility when modal opens
            loadChoiceOptions();
            loadMediaCentreItemsAndCheckDuplicates();
        }
    }, [show, mediaItem]);

    const loadChoiceOptions = async () => {
        try {
            setLoadingOptions(true);

            
            const options = await getChoiceFieldOptions();
            setChoiceOptions(options);
            

        } catch (error) {
            console.error('Failed to load choice options:', error);
            customToast.error('Failed to load form options. Using defaults.');
        } finally {
            setLoadingOptions(false);
        }
    };

    // Load Media Centre items and check for duplicates
    const loadMediaCentreItemsAndCheckDuplicates = async () => {
        if (!mediaItem) return;

        try {
            setLoadingDuplicateCheck(true);

            
            const items = await getMediaCentreItems();
            setMediaCentreItems(items);
            
            // Check for duplicates based on mediaUrl containing filename
            const currentFileName = mediaItem.name;
            
            const duplicates = items.filter(item => {
                // Simple check: does the mediaUrl field contain our filename?
                if (item.mediaUrl && typeof item.mediaUrl === 'string' && item.mediaUrl.includes(currentFileName)) {
                    return true;
                }
                return false;
            });
            
            if (duplicates.length > 0) {
                setDuplicateInfo({
                    found: true,
                    items: duplicates,
                    count: duplicates.length
                });
            } else {
                setDuplicateInfo({
                    found: false,
                    items: [],
                    count: 0
                });
            }
            
        } catch (error) {
            console.error('Failed to load Media Centre items:', error);
            // Don't show error to user, just disable duplicate check
            setDuplicateInfo(null);
        } finally {
            setLoadingDuplicateCheck(false);
        }
    };

    // Load galleries for thumbnail selection
    const loadGalleries = async () => {
        try {
            setLoadingGalleries(true);

            
            const galleriesData = await galleryService.getGalleries();
            setGalleries(galleriesData || []);
            

        } catch (error) {
            console.error('Failed to load galleries:', error);
            customToast.error('Failed to load galleries for thumbnail selection.');
        } finally {
            setLoadingGalleries(false);
        }
    };

    // Handle thumbnail selection
    const handleThumbnailSelect = (thumbnailItem) => {

        setSelectedThumbnail(thumbnailItem);
        setShowGalleryBrowser(false);
    };

    // Handle open thumbnail browser
    const handleOpenThumbnailBrowser = async () => {
        if (galleries.length === 0) {
            await loadGalleries();
        }
        setShowGalleryBrowser(true);
    };

    // Clear selected thumbnail
    const handleClearThumbnail = () => {
        setSelectedThumbnail(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Handle dropdown changes for fill-in choice fields
        if (name === 'topicName') {
            if (value === 'ADD_NEW_TOPIC') {
                setIsAddingNewTopic(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customTopicName
                }));
            } else {
                setIsAddingNewTopic(false);
                setCustomTopicName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'productService') {
            if (value === 'ADD_NEW_PRODUCT') {
                setIsAddingNewProduct(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customProductName
                }));
            } else {
                setIsAddingNewProduct(false);
                setCustomProductName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'theme') {
            if (value === 'ADD_NEW_THEME') {
                setIsAddingNewTheme(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customThemeName
                }));
            } else {
                setIsAddingNewTheme(false);
                setCustomThemeName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'entity') {
            if (value === 'ADD_NEW_ENTITY') {
                setIsAddingNewEntity(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customEntityName
                }));
            } else {
                setIsAddingNewEntity(false);
                setCustomEntityName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleCustomTopicChange = (e) => {
        const value = e.target.value;
        setCustomTopicName(value);
        setFormData(prev => ({
            ...prev,
            topicName: value
        }));
    };

    const handleCustomProductChange = (e) => {
        const value = e.target.value;
        setCustomProductName(value);
        setFormData(prev => ({
            ...prev,
            productService: value
        }));
    };

    const handleCustomThemeChange = (e) => {
        const value = e.target.value;
        setCustomThemeName(value);
        setFormData(prev => ({
            ...prev,
            theme: value
        }));
    };

    const handleCustomEntityChange = (e) => {
        const value = e.target.value;
        setCustomEntityName(value);
        setFormData(prev => ({
            ...prev,
            entity: value
        }));
    };

    const handleSeriesChange = (seriesName, isChecked) => {
        setFormData(prev => {
            if (isChecked) {
                // Add series if not already selected
                return {
                    ...prev,
                    series: [...prev.series, seriesName]
                };
            } else {
                // Remove series if unchecked
                return {
                    ...prev,
                    series: prev.series.filter(s => s !== seriesName)
                };
            }
        });
    };

    const handleBackToDropdown = () => {
        setIsAddingNewTopic(false);
        setCustomTopicName('');
        setFormData(prev => ({
            ...prev,
            topicName: ''
        }));
    };

    const handleBackToProductDropdown = () => {
        setIsAddingNewProduct(false);
        setCustomProductName('');
        setFormData(prev => ({
            ...prev,
            productService: ''
        }));
    };

    const handleBackToThemeDropdown = () => {
        setIsAddingNewTheme(false);
        setCustomThemeName('');
        setFormData(prev => ({
            ...prev,
            theme: ''
        }));
    };

    const handleBackToEntityDropdown = () => {
        setIsAddingNewEntity(false);
        setCustomEntityName('');
        setFormData(prev => ({
            ...prev,
            entity: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
                    // Validate required fields (temporarily skip series validation)
        if (!formData.shortDescription || !formData.topicName || 
                !formData.productService || !formData.theme) {
            customToast.error('Please fill in all required fields');
                setIsSubmitting(false);
                return;
            }

            // Extract just the filename from mediaUrl for webURLLink
            let videoFilename = '';
            if (mediaItem?.mediaUrl) {
                // Extract filename from various URL patterns
                if (mediaItem.mediaUrl.includes('/media/stream/')) {
                    videoFilename = mediaItem.mediaUrl.split('/media/stream/')[1];
                } else if (mediaItem.mediaUrl.includes('/')) {
                    // If it has slashes, take the last part
                    videoFilename = mediaItem.mediaUrl.split('/').pop();
                } else {
                    // If it's already just a filename, use as-is
                    videoFilename = mediaItem.mediaUrl;
                }
            }



            // Prepare media data for sending
            const mediaData = {
                shortDescription: formData.shortDescription,
                topicName: formData.topicName,
                series: formData.series,
                productService: formData.productService,
                theme: formData.theme,
                entity: formData.entity,
                duration: formData.duration,
                webURLLink: videoFilename, // Store just the filename, not the full URL
                mediaType: mediaItem?.type || 'Video',
                originalName: mediaItem?.name,
                featured: false,
                // Add thumbnail filename if selected
                thumbnail: selectedThumbnail ? selectedThumbnail.name : ''
            };



            // Call the actual API
            await sendToMediaCentre(mediaData);
            
            // Refresh choice options to include any new values that were just created
                    
            await loadChoiceOptions();

            customToast.success(`${formData.topicName} sent to Media Centre successfully!`);
            onHide();
            
            // Reset form
            setFormData({
                shortDescription: '',
                topicName: '',
                series: [], // Reset to empty array
                productService: '',
                theme: '',
                entity: 'IF',
                duration: ''
            });
            setIsAddingNewTopic(false);
            setCustomTopicName('');
            setIsAddingNewProduct(false);
            setCustomProductName('');
            setIsAddingNewTheme(false);
            setCustomThemeName('');
            setIsAddingNewEntity(false);
            setCustomEntityName('');
            
            // Clear thumbnail selection
            setSelectedThumbnail(null);

        } catch (error) {
            console.error('Error sending to Media Centre:', error);
            customToast.error('Failed to send to Media Centre: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getMediaIcon = () => {
        switch (mediaItem?.type) {
            case 'video': return faVideo;
            case 'image': return faImage;
            case 'podcast': return faPodcast;
            case 'Audio': return faPodcast;
            default: return faVideo;
        }
    };

    const getMediaTypeDisplay = () => {
        switch (mediaItem?.type) {
            case 'video': return 'Video';
            case 'image': return 'Image';
            case 'podcast': return 'Podcast';
            case 'Audio': return 'Audio';
            default: return 'Media';
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="modal-send-to-media-centre">
            <Modal.Header>
                <Modal.Title>
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                    Send to Media Centre
                </Modal.Title>
                <Button variant="link" onClick={onHide} className="p-0 border-0">
                    <FontAwesomeIcon icon={faTimes} />
                </Button>
            </Modal.Header>

            <Modal.Body>
                {mediaItem && (
                    <Alert variant="info" className="mb-4">
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={getMediaIcon()} className="me-2" />
                            <div>
                                <strong>{getMediaTypeDisplay()}:</strong> {mediaItem.name}
                                <br />
                                <small className="text-muted">
                                    This {getMediaTypeDisplay().toLowerCase()} will be published to the Member Portal Media Centre
                                </small>
                            </div>
                        </div>
                    </Alert>
                )}

                {/* Duplicate Check Status */}
                {loadingDuplicateCheck && (
                    <Alert variant="light" className="mb-3">
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            <span>Checking for existing items in Media Centre...</span>
                        </div>
                    </Alert>
                )}

                {duplicateInfo !== null && !loadingDuplicateCheck && showDuplicateWarning && duplicateInfo.found && (
                    <Alert variant="warning" className="mb-3">
                        <div className="d-flex align-items-start">
                            <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                className="me-2 mt-1" 
                            />
                            <div className="flex-grow-1">
                                {duplicateInfo.found && (
                                    <>
                                        <strong>📍 Already in Media Centre</strong>
                                        <div className="mt-2">
                                            This item is currently published in the Media Centre with the following details:
                                            {duplicateInfo.items.map((duplicate, index) => (
                                                <div key={index} className="mt-2 p-3 bg-light rounded border-start border-warning border-3">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div><strong>📝 Topic:</strong> {duplicate.topic || 'Not set'}</div>
                                                            <div><strong>🎬 Type:</strong> {duplicate.mediaType || 'Video'}</div>
                                                            <div><strong>📁 File:</strong> {duplicate.mediaUrl || 'Not set'}</div>
                                                            {duplicate.publishDate && (
                                                                                                                                  <div><strong>📅 Published:</strong> {new Date(duplicate.publishDate).toLocaleDateString('en-IE')}</div>
                                                            )}
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div><strong>📚 Series:</strong> {
                                                                Array.isArray(duplicate.series) && duplicate.series.length > 0 
                                                                    ? duplicate.series.join(', ') 
                                                                    : duplicate.series || 'Not set'
                                                            }</div>
                                                            <div><strong>🏢 Product/Service:</strong> {duplicate.productService || 'Not set'}</div>
                                                            <div><strong>🏷️ Theme:</strong> {duplicate.theme || 'Not set'}</div>
                                                            <div><strong>🏛️ Entity:</strong> {duplicate.entity || 'Not set'}</div>
                                                            {duplicate.duration && (
                                                                <div><strong>⏱️ Duration:</strong> {duplicate.duration}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {duplicate.shortDescription && (
                                                        <div className="mt-2">
                                                            <strong>📄 Description:</strong> 
                                                            <div className="text-muted small mt-1">{duplicate.shortDescription}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <small className="text-dark">
                                                    <strong>💡 Options:</strong>
                                                    <ul className="mb-0 mt-1">
                                                        <li>Proceed to <strong>update the existing item</strong> with new details</li>
                                                        <li>Change the topic name to <strong>create a new version</strong></li>
                                                        <li>Cancel if you don't want to make changes</li>
                                                    </ul>
                                                </small>

                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={() => setShowDuplicateWarning(false)}
                                style={{ fontSize: '0.8rem' }}
                            ></button>
                        </div>
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Topic Name <span className="text-danger">*</span></Form.Label>
                                {isAddingNewTopic ? (
                                    <div className="custom-topic-input d-flex gap-2">
                                <Form.Control
                                    type="text"
                                            name="topicName"
                                            value={customTopicName}
                                            onChange={handleCustomTopicChange}
                                            placeholder="Enter new topic name..."
                                            required
                                            autoFocus
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={handleBackToDropdown}
                                            title="Back to topic list"
                                        >
                                            ↩️ Back
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="topicName"
                                        value={formData.topicName}
                                    onChange={handleInputChange}
                                    required
                                        disabled={loadingOptions}
                                    >
                                        <option value="">
                                            {loadingOptions ? 'Loading topics...' : 'Select Topic Name'}
                                        </option>
                                        {choiceOptions.topicNames && choiceOptions.topicNames.length > 0 ? (
                                            choiceOptions.topicNames.map(topic => (
                                                <option key={topic} value={topic}>{topic}</option>
                                            ))
                                        ) : (
                                            <option disabled>No topics found</option>
                                        )}
                                        <option value="ADD_NEW_TOPIC" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                            ➕ Add New Topic...
                                        </option>
                                    </Form.Select>
                                )}
                                {loadingOptions && (
                                    <Form.Text className="text-muted">
                                        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                        Loading options from SharePoint...
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Short Description <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="shortDescription"
                                    value={formData.shortDescription}
                                    onChange={handleInputChange}
                                    placeholder="Brief description of the content"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Series <span className="text-danger">*</span></Form.Label>
                                {loadingOptions ? (
                                    <div className="text-muted">
                                        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                        Loading series options...
                                    </div>
                                ) : (
                                    <div className="series-selection border rounded p-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                        {choiceOptions.series.length > 0 ? (
                                            choiceOptions.series.map(series => (
                                                <Form.Check
                                                    key={series}
                                                    type="checkbox"
                                                    id={`series-${series}`}
                                                    label={series}
                                                    checked={formData.series.includes(series)}
                                                    onChange={(e) => handleSeriesChange(series, e.target.checked)}
                                                    className="mb-1"
                                                />
                                            ))
                                        ) : (
                                            <div className="text-muted">No series found</div>
                                        )}
                                    </div>
                                )}
                                <Form.Text className="text-muted">
                                    {formData.series.length > 0 
                                        ? `Selected: ${formData.series.join(', ')}` 
                                        : 'Select one or more series (required)'
                                    }
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Product/Service <span className="text-danger">*</span></Form.Label>
                                {!isAddingNewProduct ? (
                                <Form.Select
                                    name="productService"
                                    value={formData.productService}
                                    onChange={handleInputChange}
                                    required
                                        disabled={loadingOptions}
                                    >
                                        <option value="">
                                            {loadingOptions ? 'Loading products...' : 'Select Product/Service'}
                                        </option>
                                        {choiceOptions.products && choiceOptions.products.length > 0 ? (
                                            choiceOptions.products.map(product => (
                                        <option key={product} value={product}>{product}</option>
                                            ))
                                        ) : (
                                            <option disabled>No products found</option>
                                        )}
                                        <option value="ADD_NEW_PRODUCT" style={{borderTop: '1px solid #ccc', fontStyle: 'italic'}}>
                                            ➕ Add New Product/Service...
                                        </option>
                                </Form.Select>
                                ) : (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customProductName}
                                            onChange={handleCustomProductChange}
                                            placeholder="Enter new product/service name"
                                            autoFocus
                                            required
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={handleBackToProductDropdown}
                                            title="Back to dropdown"
                                        >
                                            ↩️
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Theme <span className="text-danger">*</span></Form.Label>
                                {!isAddingNewTheme ? (
                                <Form.Select
                                    name="theme"
                                    value={formData.theme}
                                    onChange={handleInputChange}
                                    required
                                        disabled={loadingOptions}
                                    >
                                        <option value="">
                                            {loadingOptions ? 'Loading themes...' : 'Select Theme'}
                                        </option>
                                        {choiceOptions.themes && choiceOptions.themes.length > 0 ? (
                                            choiceOptions.themes.map(theme => (
                                        <option key={theme} value={theme}>{theme}</option>
                                            ))
                                        ) : (
                                            <option disabled>No themes found</option>
                                        )}
                                        <option value="ADD_NEW_THEME" style={{borderTop: '1px solid #ccc', fontStyle: 'italic'}}>
                                            ➕ Add New Theme...
                                        </option>
                                </Form.Select>
                                ) : (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customThemeName}
                                            onChange={handleCustomThemeChange}
                                            placeholder="Enter new theme name"
                                            autoFocus
                                            required
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={handleBackToThemeDropdown}
                                            title="Back to dropdown"
                                        >
                                            ↩️
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Entity</Form.Label>
                                {!isAddingNewEntity ? (
                                <Form.Select
                                    name="entity"
                                    value={formData.entity}
                                    onChange={handleInputChange}
                                        disabled={loadingOptions}
                                    >
                                        <option value="">
                                            {loadingOptions ? 'Loading entities...' : 'Select Entity'}
                                        </option>
                                        {choiceOptions.entities && choiceOptions.entities.length > 0 ? (
                                            choiceOptions.entities.map(entity => (
                                        <option key={entity} value={entity}>{entity}</option>
                                            ))
                                        ) : (
                                            <option disabled>No entities found</option>
                                        )}
                                        <option value="ADD_NEW_ENTITY" style={{borderTop: '1px solid #ccc', fontStyle: 'italic'}}>
                                            ➕ Add New Entity...
                                        </option>
                                </Form.Select>
                                ) : (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customEntityName}
                                            onChange={handleCustomEntityChange}
                                            placeholder="Enter new entity name"
                                            autoFocus
                                            required
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={handleBackToEntityDropdown}
                                            title="Back to dropdown"
                                        >
                                            ↩️
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                        <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Media Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={getMediaTypeDisplay()}
                                    disabled
                                    className="bg-light"
                                />
                                <Form.Text className="text-muted">
                                    Automatically detected from file type
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        
                        {(mediaItem?.type === 'video' || mediaItem?.type === 'podcast' || mediaItem?.type === 'Audio' || mediaItem?.type === 'audio') && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Duration</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="duration"
                                        value={formData.duration}
                                        onChange={handleInputChange}
                                        placeholder="e.g., 12 min"
                                    />
                                    <Form.Text className="text-muted">
                                        Optional - format like "12 min" or "1h 30m"
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    {/* Thumbnail Selection Section - only for videos and audio */}
                    {mediaItem?.type !== 'image' && (
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Custom Thumbnail (Optional)</Form.Label>
                                {selectedThumbnail ? (
                                    <div className="selected-thumbnail-preview border rounded p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <div className="thumbnail-preview me-3" style={{ width: '60px', height: '60px' }}>
                                                    <ThumbnailPreview thumbnailItem={selectedThumbnail} />
                                                </div>
                                                <div>
                                                    <div className="fw-bold">{selectedThumbnail.title || selectedThumbnail.name}</div>
                                                    <div className="text-muted small">Custom thumbnail selected</div>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={handleOpenThumbnailBrowser}
                                                    disabled={loadingGalleries}
                                                >
                                                    <FontAwesomeIcon icon={faImage} className="me-1" />
                                                    Change
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    onClick={handleClearThumbnail}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="thumbnail-selection border rounded p-3">
                                        <div className="text-center">
                                            <div className="mb-2">
                                                <FontAwesomeIcon icon={faImage} size="2x" className="text-muted" />
                                            </div>
                                            <div className="mb-2">
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={handleOpenThumbnailBrowser}
                                                    disabled={loadingGalleries}
                                                >
                                                    {loadingGalleries ? (
                                                        <>
                                                            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                                            Loading Galleries...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faImage} className="me-1" />
                                                            Select Thumbnail
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <div className="text-muted small">
                                                Choose a custom thumbnail image from the galleries
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <Form.Text className="text-muted">
                                    Optional: Select a custom thumbnail image that will be displayed in the Media Centre
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faPaperPlane} spin className="me-2" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                                Send to Media Centre
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>

            {/* Gallery Browser Modal */}
            {showGalleryBrowser && (
                <GalleryBrowser
                    galleries={galleries}
                    mediaType="image"
                    onSelect={handleThumbnailSelect}
                    onClose={() => setShowGalleryBrowser(false)}
                    onRefreshGalleries={loadGalleries}
                />
            )}
        </Modal>
    );
};

export default ModalSendToMediaCentre; 